import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import AsNumeral from '../vendors/asNumeral';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box as CotopaxiBox, GiftVoucher, Heading, HeadingPriority, HeadingType, PDPLayout, PDPLayoutItemArea, Text, TextSize, } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
import { GiftVoucherMainSlider } from '../components/gift-voucher/GiftVoucherMainSlider';
import { GiftVoucherThumbSlider } from '../components/gift-voucher/GiftVoucherThumbSlider';
import { GiftVoucherForm } from '../components/gift-voucher/gift-voucher-form/GiftVoucherForm';
import { basketActions } from '../redux/basket/actions/BasketActions.creators';
import { analyticsActions } from '../redux/analytics/actions/AnalyticsActions.creators';
import { makeGetBasketPopupCollapseState } from '../redux/basket/selectors/BasketSelector';
import { makeGetProductDefaults } from '../redux/product/selectors/ProductSelector';
import BasketPopupContainer from './BasketPopupContainer';
import AdvantagesBlock from '../components/product-detail/advantages-block/AdvantagesBlock';
import { getImageFromCdn } from '../util/ImageUtil';
import MonetateUtil from '../util/MonetateUtil';
import { Box, Divider, useMediaQueryMatchesOnMobile, useMediaQueryMatchesOnDesktop } from '@yonderland/andes-react';
const TEXTAREA_ROWS = {
    DESKTOP: 5,
    MOBILE: 10,
};
const GiftVoucherContainer = ({ title, subtitle, amountConfig, basketPopUp, hideImageOverlay, advantages, lazyLoadedImages, lazyLoadedLogo, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.general);
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const [textareaRows, setTextareaRow] = useState(TEXTAREA_ROWS.DESKTOP);
    const [amount, setAmount] = useState();
    const [message, setMessage] = useState();
    const [formActions, setFormActions] = useState();
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const isMobile = useMediaQueryMatchesOnMobile();
    const { currency, mainWebShop, product: productConfig, imageCdnPrefix } = useAemConfig();
    const voucherSizeCode = productConfig.voucher.productSize;
    const dispatch = useDispatch();
    const isBasketPopupCollapsed = useSelector(makeGetBasketPopupCollapseState());
    const { productId } = useSelector(makeGetProductDefaults());
    const onActiveSlideIndexChange = (newActiveSlideIndex) => {
        setActiveSlideIndex(newActiveSlideIndex);
    };
    const giftVoucherTranslations = {
        example: t(`${I18N_NAMESPACES.general}:voucher.example`),
        disclaimer: (fascia) => t([
            `${I18N_NAMESPACES.general}:voucher.disclaimer.${fascia}`,
            `${I18N_NAMESPACES.general}:voucher.disclaimer.unspecific`,
        ], { fascia }),
        webcode: t(`${I18N_NAMESPACES.general}:voucher.webcode`),
        date: t(`${I18N_NAMESPACES.general}:voucher.date`),
    };
    const addVoucher = (values, actions) => {
        const image = lazyLoadedImages[activeSlideIndex].imagePath;
        let templateName = image.substr(Number(image.lastIndexOf('/')) + 1);
        templateName = templateName.substring(0, templateName.indexOf('.'));
        dispatch(basketActions.createAddVoucherAction({
            voucherSizeCode,
            image,
            amount: Number(values.amount),
            quantity: 1,
            message: values.message,
            imageTemplateName: templateName,
        }));
        if (!formActions) {
            setFormActions(actions);
        }
    };
    useEffect(() => {
        setTextareaRow(isDesktop ? TEXTAREA_ROWS.DESKTOP : TEXTAREA_ROWS.MOBILE);
    }, [isDesktop, isMobile]);
    useEffect(() => {
        if (isBasketPopupCollapsed && formActions) {
            formActions.resetForm();
            setAmount('');
            setMessage('');
        }
    }, [isBasketPopupCollapsed]);
    useEffect(() => {
        dispatch(analyticsActions.giftVoucherInit(currency));
        MonetateUtil.pushProductDetailEvent(productId);
    }, []);
    if (!lazyLoadedImages) {
        return null;
    }
    return (_jsx(PDPLayout, { children: _jsxs(CotopaxiBox, { mobile: { spacing: { marginTop: 2, marginBottom: 4 } }, tablet: { spacing: { marginTop: 3, marginBottom: 0 } }, desktop: { spacing: { marginTop: 0 } }, children: [_jsxs(PDPLayout.Item, { area: PDPLayoutItemArea.HEADER, children: [_jsx(CotopaxiBox, { mobile: { spacing: { marginBottom: 2 } }, children: _jsx(Heading, { priority: HeadingPriority.H1, type: HeadingType.TITLE, children: title }) }), _jsx(CotopaxiBox, { mobile: { spacing: { marginBottom: 3 } }, tablet: { spacing: { marginBottom: 1 } }, children: _jsx(Text, { inline: true, size: TextSize.SMALL, children: subtitle }) })] }), _jsxs(PDPLayout.Item, { area: PDPLayoutItemArea.PREVIEW, children: [_jsx(GiftVoucherMainSlider, { activeSlideIndex: activeSlideIndex, brand: mainWebShop, images: lazyLoadedImages, onActiveSlideIndexChange: onActiveSlideIndexChange, hideImageOverlay: hideImageOverlay, imageCdnPrefix: imageCdnPrefix }), _jsx(GiftVoucher, { amount: amount && AsNumeral.formatAsCurrency(Number(amount)), currency: currency, logo: lazyLoadedLogo ? getImageFromCdn(lazyLoadedLogo, imageCdnPrefix) : '', message: message, mainWebShop: mainWebShop, translations: giftVoucherTranslations })] }), _jsxs(PDPLayout.Item, { area: PDPLayoutItemArea.INTERACTION_BLOCK, children: [_jsxs(CotopaxiBox, { mobile: { spacing: { marginBottom: 4 } }, children: [_jsx(Text, { inline: true, size: TextSize.SMALL, emphasized: true, children: t(`${I18N_NAMESPACES.general}:voucher.selectImage.label`) }), _jsx(GiftVoucherThumbSlider, { images: lazyLoadedImages, imageCdnPrefix: imageCdnPrefix, activeSlideIndex: activeSlideIndex, onActiveSlideIndexChange: onActiveSlideIndexChange })] }), _jsx(GiftVoucherForm, { currency: currency, minAmount: amountConfig.minAmount, maxAmount: amountConfig.maxAmount, onSubmit: addVoucher, onAmountChange: setAmount, onMessageChange: setMessage, textareaRows: textareaRows }), advantages && (_jsxs(_Fragment, { children: [_jsx(Box, { paddingBlock: { base: '4', lg: '6' }, children: _jsx(Divider, {}) }), _jsx(AdvantagesBlock, { advantages: advantages })] }))] }), _jsx(BasketPopupContainer, { recommendations: basketPopUp })] }) }));
};
export default GiftVoucherContainer;
